(ns app.front.view.group.admin.fees.template.template-subs
  (:require [app.domain.fee.formula :as formula]
            [app.domain.fee.model :as fee-model]
            [app.front.entities :as entities]
            [app.front.view.group.admin.fees.template.template-events :as ft-events]
            [app.front.view.group.admin.fees.util.fee-template :as fee-template]
            [app.front.view.group.admin.fees.util.fee-utils :as fee-utils]
            [app.front.view.group.admin.form-field-selection :as ff-selection]
            [app.front.view.group.admin.subs :as gas]
            [app.i18n.model :as i18n-model]
            [clojure.string :as str]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))


(rf/reg-sub
  ::creating-new?
  (fn [db _]
    (= (-> db ::ft-events/fee-templates :selected-fee-template) :new)))


(rf/reg-sub
  ::unsaved-changes
  (fn [db _]
    (-> db ::ft-events/fee-templates :unsaved-changes)))


(rf/reg-sub ::current-template-id
  :<- [::selected-fee-template]
  :<- [::creating-new?]
  fee-utils/current-id-handler)

(rf/reg-sub ::current-template-changes
  :<- [::unsaved-changes]
  :<- [::current-template-id]
  fee-utils/changes-handler)

(rf/reg-sub ::fee-template-for-edit
  :<- [::selected-fee-template]
  (fn [fee-template _]
    (fee-utils/strings-map [[:fee-template/titles :fee-template/title]]
                           fee-template)))

(rf/reg-sub ::fee-attrs-with-changes
  :<- [::fee-template-for-edit]
  :<- [::current-template-changes]
  (fn [[fee-template changes] _]
    (fee-utils/get-props-with-changes fee-template
                                      fee-template/all-fields
                                      changes)))


(rf/reg-sub ::editor-disabled?
  (fn [db _]
    (-> db ::ft-events/fee-templates :disabled? boolean)))


(rf/reg-sub ::fee-schedule
  (fn [db _]
    (-> db ::ft-events/fee-templates :fee-schedule)))


(rf/reg-sub ::close-callback-event
  (fn [db _]
    (-> db ::ft-events/fee-templates :close-callback-event)))


(rf/reg-sub ::fee-variables
  :<- [::entities/context]
  :<- [::fee-schedule]
  (fn [[ctx fee-schedule] _]
    (entities/find-entities ctx {:entity/parent (:db/id fee-schedule)
                                 :entity/type   :entity.type/fee-variable})))


(rf/reg-sub ::available-fee-variables
  :<- [::selected-fee-template-id]
  :<- [::fee-variables]
  (fn [[template-id fee-variables] _]
    (->> fee-variables
         (filter (fn [{:fee-variable/keys [fee-template]}]
                   (not= template-id (:db/id fee-template)))))))


(rf/reg-sub ::fee-templates
  :<- [::entities/context]
  :<- [::fee-schedule]
  (fn [[ctx fee-schedule] _]
    (entities/find-entities ctx {:entity/parent (:db/id fee-schedule)
                                 :entity/type   :entity.type/fee-template})))


(rf/reg-sub ::fee-template-list
  :<- [::fee-templates]
  :<- [::selected-fee-template-id]
  :<- [:language]
  (fn [[fee-templates selected-id language] _]
    (->> fee-templates
         (map (fn [tpl] (p/assoc-if {:id    (:db/id tpl)
                                     :title (fee-model/fee-template-title tpl language)}
                                    :selected? (= (:db/id tpl) selected-id))))
         (sort-by (comp str/lower-case (some-fn :title (constantly "")))))))


(rf/reg-sub ::selected-fee-template-id
  (fn [db _]
    (-> db ::ft-events/fee-templates :selected-fee-template)))


(rf/reg-sub ::selected-fee-template
  :<- [::selected-fee-template-id]
  :<- [::fee-templates]
  (fn [[id fee-templates] _]
    (p/find-first fee-templates {:db/id id})))


(rf/reg-sub ::fee-template-errors
  (fn [db _]
    (get-in db [::ft-events/fee-templates :errors])))


(rf/reg-sub ::save-disabled?
  :<- [::selected-fee-template]
  :<- [::unsaved-changes]
  :<- [::creating-new?]
  :<- [::quantity-formula]
  :<- [::unit-price-formula]
  :<- [::gas/group-features]
  :<- [::fee-template-errors]
  (fn save-disabled? [[fee-template unsaved-changes creating-new? quantity-formula unit-price-formula group-features fee-template-errors] _]
    (let [id                                               (if creating-new?
                                                             :new
                                                             (:db/id fee-template))
          {:fee-template/keys [allow-penalty?
                               penalty-rate
                               interest-rate] :as changes} (get unsaved-changes id)
          get-value                                        (fee-utils/get-value-fn fee-template changes)
          has-value?                                       #(not (i18n-model/blank? (get-value %)))
          fields                                           (concat (fee-template/required-fields
                                                                     allow-penalty?
                                                                     penalty-rate
                                                                     interest-rate)
                                                                   (when (contains? group-features
                                                                                    :group.feature/fee-integration)
                                                                     fee-template/required-for-integration))
          fields-missing?                                  (->> (if creating-new? fields (keys changes))
                                                                (every? has-value?)
                                                                not)
          changes?                                         (some? changes)
          save-enabled?                                    (and changes? (not fields-missing?))
          cycle-error?                                     (get-in fee-template-errors [id :cycle])]
      (-> (or (not save-enabled?)
              (:invalid? quantity-formula)
              (:loading? quantity-formula)
              (:invalid? unit-price-formula)
              (:loading? unit-price-formula)
              (some? cycle-error?))
          boolean))))


(rf/reg-sub ::formula-status
  (fn [db [_ formula]]
    (let [status (get-in db [::ft-events/fee-templates :formulas formula :status])]
      (if (some? status)
        status
        :not-checked))))


(rf/reg-sub ::formula-message
  (fn [db [_ formula]]
    (get-in db [::ft-events/fee-templates :formulas formula :message])))


(rf/reg-sub ::unsaved-formula
  :<- [::current-template-changes]
  (fn [unsaved-changes [_ formula]]
    (get unsaved-changes formula)))


(rf/reg-sub ::quantity-formula
  :<- [::selected-fee-template]
  :<- [::available-fee-variables]
  :<- [::unsaved-formula :fee-template/quantity-formula]
  :<- [::formula-status :fee-template/quantity-formula]
  :<- [::formula-message :fee-template/quantity-formula]
  :<- [:tr]
  (fn [[fee-template fee-variables unsaved-quantity-formula status message tr] _]
    (let [formula (if (some? unsaved-quantity-formula)
                    unsaved-quantity-formula
                    (:fee-template/quantity-formula fee-template))]
      (assoc (formula/parse-formula formula fee-variables status message tr)
             :loading? (= :loading status)))))


(rf/reg-sub ::unit-price-formula
  :<- [::selected-fee-template]
  :<- [::available-fee-variables]
  :<- [::unsaved-formula :fee-template/unit-price-formula]
  :<- [::formula-status :fee-template/unit-price-formula]
  :<- [::formula-message :fee-template/unit-price-formula]
  :<- [:tr]
  (fn [[fee-template fee-variables unsaved-unit-price-formula status message tr] _]
    (let [formula (if (some? unsaved-unit-price-formula)
                    unsaved-unit-price-formula
                    (:fee-template/unit-price-formula fee-template))]
      (assoc (formula/parse-formula formula fee-variables status message tr)
             :loading? (= :loading status)))))


(defn get-first-variable [formula]
  (let [formula (:formula formula)]
    (when formula
      (formula/first-variable formula))))


(rf/reg-sub ::first-quantity-variable
  :<- [::quantity-formula]
  get-first-variable)


(rf/reg-sub ::first-unit-price-variable
  :<- [::unit-price-formula]
  get-first-variable)


(rf/reg-sub ::unsaved-system-of-units
  :<- [::current-template-changes]
  :-> :fee-template/system-of-units)


(rf/reg-sub ::source-field
  :<- [::fee-variables]
  (fn [fee-variables [_ first-variable]]
    (->> fee-variables
         (filter (fn [variable]
                   (= (:fee-variable/identifier variable) first-variable)))
         first
         :fee-variable/source-field)))


(rf/reg-sub ::form-unit
  (fn [[_ first-variable] _]
    [(rf/subscribe [::ff-selection/all-forms-with-fields])
     (rf/subscribe [::source-field first-variable])])
  (fn [[forms+fields source-field] _]
    (->> forms+fields
         (map #(filter (fn [field]
                         (= (:field field) source-field))
                       %))
         (remove empty?)
         first
         first
         :unit)))


(rf/reg-sub ::quantity-formula-variables
  :<- [::quantity-formula]
  :<- [::available-fee-variables]
  (fn [[formula fee-variables] _]
    (let [identifiers (map :fee-variable/identifier fee-variables)]
      (->> formula
           :tokens
           (filter #(formula/variable? % identifiers))))))


(rf/reg-sub ::quantity-formula-variable-count
  :<- [::quantity-formula-variables]
  :-> count)


(rf/reg-sub ::unit-enabled?
  :<- [::current-template-changes]
  :-> :fee-template/unit-enabled?)
